import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../AxiosInterceptor/axiosInterceptor";
import { TRIGGER_DBX_JOB_QUERY } from "./DataBricksQuery";

const initialState = {
    data: [],
    loading: false,
    error: false,
    selectedRows: [],
    // Databricks job state
    jobLoading: false,
    jobStatus: null,
    jobRunId: null,
    jobId: null,
    jobNumberInJob: null,
    jobErrorMessage: null
}

// Trigger Databricks job without carriers
export const triggerDatabricksJobData = createAsyncThunk('get/triggerDatabricksJobData',
    async (tabIndex, { rejectWithValue }) => {
        try {
            const selectedCarriers = [];
            let jobKeyId = '';
            switch (tabIndex) {
                case 0:
                    jobKeyId = 'CocaReport1JobID';
                    break;
                case 1:
                    jobKeyId = 'CocaReport2JobID';
                    break;
                case 2:
                    jobKeyId = 'CocaReport3JobID';
                    break; // added break to prevent fallthrough
                default:
                    jobKeyId = '';
                    break;
            }
            // Primary attempt: GraphQL payload
            const gqlPayload = {
                query: TRIGGER_DBX_JOB_QUERY,
                variables: {
                    request: {
                        jobKey: jobKeyId,
                        tabName: 'CocaReport',
                        selectedCarriers,
                        searchCriteria: ''
                    }
                }
            };

            let json;
            try {
                const { data } = await axiosInstance.post(process.env.REACT_APP_DATABRICKS, gqlPayload);
                json = data;
            } catch (networkErr) {
                console.error('[DatabricksTrigger] GraphQL request failed, will try fallback format:', networkErr);
            }

            // Interpret response (GraphQL or simple)
            let result = json?.data?.TriggerDatabricksJob || json?.TriggerDatabricksJob || json; // be permissive
            if (!result || typeof result !== 'object') {
                console.error('[DatabricksTrigger] Unexpected response shape', json);
                return rejectWithValue('Databricks job trigger failed');
            }
            if (result.success === false) {
                return rejectWithValue(result.errorMessage || 'Job failed');
            }
            return result;
        } catch (err) {
            return rejectWithValue(err.message || 'Databricks job trigger failed');
        }
    }
);

//Trigger Databricks job with carriers
// Trigger Databricks job WITH selected carriers (object param supports backward compatibility if number passed)
export const triggerDatabricksJobWithCarrierData = createAsyncThunk('get/triggerDatabricksJobWithCarrierData',
    async (params, { rejectWithValue }) => {
        try {
            // Legacy numeric or object param support
            let tabIndex; let selectedCarriers;
            if (typeof params === 'number') {
                tabIndex = params;
                selectedCarriers = [];
            } else {
                tabIndex = params?.tabIndex ?? 0;
                selectedCarriers = Array.isArray(params?.selectedCarriers) ? params.selectedCarriers : [];
            }
            // Normalize carriers
            selectedCarriers = selectedCarriers
                .map(c => (typeof c === 'string' ? c : (c?.carrier || '')))
                .filter(c => typeof c === 'string' && c.trim() !== '');

            // Derive jobKey based on tabIndex
            let jobKeyId = '';
            switch (tabIndex) {
                case 0:
                    jobKeyId = 'CocaReport1JobID';
                    break;
                case 1:
                    jobKeyId = 'CocaReport2JobID';
                    break;
                case 2:
                    jobKeyId = 'CocaReport3JobID';
                    break;
                default:
                    jobKeyId = '';
            }
            const gqlPayload = {
                query: TRIGGER_DBX_JOB_QUERY,
                variables: {
                    request: {
                        jobKey: jobKeyId,
                        tabName: 'CocaReport',
                        selectedCarriers,
                        searchCriteria: ''
                    }
                }
            };
            let json;
            try {
                const { data } = await axiosInstance.post(process.env.REACT_APP_DATABRICKS, gqlPayload);
                json = data;
            } catch (networkErr) {
                console.error('[DatabricksTrigger][WithCarriers] GraphQL request failed, attempting fallback:', networkErr);
                const simplePayload = {
                    request: {
                        jobKey: jobKeyId,
                        tabName: 'CocaReport',
                        selectedCarriers,
                        searchCriteria: ''
                    }
                };
                try {
                    const { data } = await axiosInstance.post(process.env.REACT_APP_DATABRICKS, simplePayload);
                    json = data;
                } catch (fallbackErr) {
                    return rejectWithValue(fallbackErr.message || 'Network failure triggering job');
                }
            }

            let result = json?.data?.TriggerDatabricksJob || json?.TriggerDatabricksJob || json;
            if (!result || typeof result !== 'object') {
                console.error('[DatabricksTrigger][WithCarriers] Unexpected response shape', json);
                return rejectWithValue('Databricks job trigger failed');
            }
            if (result.success === false) {
                return rejectWithValue(result.errorMessage || 'Job failed');
            }
            return result;
        } catch (err) {
            return rejectWithValue(err.message || 'Databricks job trigger failed');
        }
    }
);

const cocaReportDataBricksSlice = createSlice({
    name: 'cocaDataBricks',
    initialState,
    extraReducers: (builder) => {
        builder
            // Databricks job trigger lifecycle
            .addCase(triggerDatabricksJobData.pending, (state) => {
                state.jobLoading = true;
                state.jobErrorMessage = null;
            })
            .addCase(triggerDatabricksJobData.fulfilled, (state, action) => {
                state.jobLoading = false;
                state.jobStatus = action.payload.status;
                state.jobRunId = action.payload.runId;
                state.jobId = action.payload.jobId;
                state.jobNumberInJob = action.payload.numberInJob;
                state.jobErrorMessage = null;
            })
            .addCase(triggerDatabricksJobData.rejected, (state, action) => {
                state.jobLoading = false;
                state.jobErrorMessage = action.payload || 'Job trigger failed';
            })
            // With carrier variant
            .addCase(triggerDatabricksJobWithCarrierData.pending, (state) => {
                state.jobLoading = true;
                state.jobErrorMessage = null;
            })
            .addCase(triggerDatabricksJobWithCarrierData.fulfilled, (state, action) => {
                state.jobLoading = false;
                state.jobStatus = action.payload.status;
                state.jobRunId = action.payload.runId;
                state.jobId = action.payload.jobId;
                state.jobNumberInJob = action.payload.numberInJob;
                state.jobErrorMessage = null;
            })
            .addCase(triggerDatabricksJobWithCarrierData.rejected, (state, action) => {
                state.jobLoading = false;
                state.jobErrorMessage = action.payload || 'Job trigger failed';
            });
    }
})

export default cocaReportDataBricksSlice.reducer
